import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Minus, ShoppingBag, CheckCircle2, Download } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api, { inr, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import jsPDF from "jspdf";

export default function CartDrawer() {
  const { items, setQty, remove, total, open, setOpen, clear } = useCart();
  const [stores, setStores] = useState([]);
  const [step, setStep] = useState("cart"); // cart | checkout | done
  const [confirmed, setConfirmed] = useState(null);
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", store_id: "", fulfilment: "pickup", address: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.customer_name || !form.phone || !form.store_id) {
      toast.error("Please fill your name, phone and select a store");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", { ...form, items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, qty: i.qty })) });
      setConfirmed(data);
      setStep("done");
      clear();
      toast.success("Order request placed!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const invoice = () => {
    if (!confirmed) return;
    const doc = new jsPDF();
    doc.setFontSize(18); doc.text("D K BAZAAR — Order Request", 14, 20);
    doc.setFontSize(11);
    doc.text(`Reference: ${confirmed.reference}`, 14, 32);
    doc.text(`Customer: ${confirmed.customer_name}`, 14, 40);
    doc.text(`Store: ${confirmed.store_name}`, 14, 48);
    doc.text(`Fulfilment: ${confirmed.fulfilment}`, 14, 56);
    let y = 70; doc.text("Items", 14, y); y += 8;
    confirmed.items.forEach((it) => { doc.text(`${it.name}  x${it.qty}`, 14, y); doc.text(`Rs. ${it.price * it.qty}`, 160, y); y += 8; });
    y += 4; doc.setFontSize(13); doc.text(`Total: Rs. ${confirmed.total}`, 14, y);
    doc.save(`${confirmed.reference}.pdf`);
  };

  const close = () => { setOpen(false); setTimeout(() => { setStep("cart"); setConfirmed(null); }, 300); };

  return (
    <Sheet open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-yellow-600" />
            {step === "done" ? "Order Confirmed" : step === "checkout" ? "Checkout" : "Your Cart"}
          </SheetTitle>
        </SheetHeader>

        {step === "cart" && (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-3 no-scrollbar">
              {items.length === 0 && <p className="text-center text-muted-foreground py-16">Your cart is empty.</p>}
              {items.map((i) => (
                <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-3 items-center border border-slate-200 dark:border-slate-800 rounded-xl p-3">
                  <img src={i.image} alt={i.name} className="w-14 h-14 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{i.name}</p>
                    <p className="text-xs text-muted-foreground">{inr(i.price)} · {i.unit}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="p-1 rounded border" onClick={() => setQty(i.product_id, i.qty - 1)}><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center text-sm">{i.qty}</span>
                    <button className="p-1 rounded border" onClick={() => setQty(i.product_id, i.qty + 1)}><Plus className="w-3 h-3" /></button>
                  </div>
                  <button onClick={() => remove(i.product_id)} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            {items.length > 0 && (
              <div className="border-t border-slate-200 dark:border-slate-800 pt-4 space-y-3">
                <div className="flex justify-between font-heading font-bold text-lg"><span>Total</span><span data-testid="cart-total">{inr(total)}</span></div>
                <Button data-testid="proceed-checkout-btn" onClick={() => setStep("checkout")} className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900">Proceed to Order Request</Button>
              </div>
            )}
          </>
        )}

        {step === "checkout" && (
          <div className="flex-1 overflow-y-auto py-4 space-y-4 no-scrollbar">
            <div><Label>Full Name</Label><Input data-testid="checkout-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></div>
            <div><Label>Phone</Label><Input data-testid="checkout-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><Label>Email (optional)</Label><Input data-testid="checkout-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div>
              <Label>Select Store</Label>
              <Select value={form.store_id} onValueChange={(v) => set("store_id", v)}>
                <SelectTrigger data-testid="checkout-store"><SelectValue placeholder="Choose a store" /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Fulfilment</Label>
              <RadioGroup value={form.fulfilment} onValueChange={(v) => set("fulfilment", v)} className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="pickup" data-testid="fulfil-pickup" /> Store Pickup</label>
                <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="delivery" data-testid="fulfil-delivery" /> Home Delivery</label>
              </RadioGroup>
            </div>
            {form.fulfilment === "delivery" && (
              <div><Label>Delivery Address</Label><Input data-testid="checkout-address" value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            )}
            <div className="flex justify-between font-heading font-bold pt-2 border-t"><span>Total</span><span>{inr(total)}</span></div>
            <p className="text-xs text-muted-foreground">This is an order request. No payment is taken now — the store will confirm and contact you.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("cart")} className="flex-1">Back</Button>
              <Button data-testid="place-order-btn" onClick={submit} disabled={submitting} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-slate-900">{submitting ? "Placing…" : "Place Request"}</Button>
            </div>
          </div>
        )}

        {step === "done" && confirmed && (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-10 space-y-4" data-testid="order-confirmation">
            <CheckCircle2 className="w-16 h-16 text-yellow-600" />
            <h3 className="font-heading text-xl font-bold">Order Request Received!</h3>
            <p className="text-sm text-muted-foreground">Your reference number</p>
            <p className="font-mono text-lg font-bold text-yellow-700 bg-yellow-100 dark:bg-yellow-950 px-4 py-2 rounded-lg">{confirmed.reference}</p>
            <p className="text-sm text-muted-foreground max-w-xs">Save this to track your order. The {confirmed.store_name} team will confirm shortly.</p>
            <div className="flex gap-2 w-full pt-4">
              <Button variant="outline" onClick={invoice} className="flex-1"><Download className="w-4 h-4 mr-2" /> Invoice</Button>
              <Button onClick={close} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-slate-900">Done</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
